import React, { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import FormHeader from "@/components/FormHeader";
import Modal from "@/components/Modal";
import api from "@/lib/axios";
import { DataPendaftaran } from "@/types";
import { AxiosError } from "axios";
import { Loader2, MoreHorizontal } from "lucide-react";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import FormPeriodeCustom from "./FormPeriodeCustom";
import FormPindahProdi from "./FormPindahProdi";
import { IoLogIn } from "react-icons/io5";

const patchDataUnvalidIsiUkt = async ({
  id_data_pendaftaran,
}: {
  id_data_pendaftaran: string;
}) => {
  const response = await api.patch("/data-pendaftaran/unvalid-isi-ukt", {
    id_data_pendaftaran,
  });

  return response.data;
};
const postDataSendNotifEmailValidIsiUkt = async ({
  id_data_pendaftaran,
}: {
  id_data_pendaftaran: string;
}) => {
  const response = await api.post(
    "/data-pendaftaran/notif/send-email-valid-isi-ukt",
    {
      id_data_pendaftaran,
    },
  );

  return response.data;
};

const loginBypassMahasiswa = async (email: string) => {
  const response = await api.post("/auth/login-bypass-mahasiswa", {
    email,
  });

  return response?.data;
};

const patchDataUnvalidStatusPendaftaran = async ({
  id_data_pendaftaran,
}: {
  id_data_pendaftaran: string;
}) => {
  const response = await api.patch("/data-pendaftaran/unvalid-pendaftaran", {
    id_data_pendaftaran,
  });

  return response.data;
};

export default function Actions({ row }: { row: DataPendaftaran }) {
  const [
    showConfirmationUnvalidPendaftaran,
    setShowConfirmationUnvalidPendaftaran,
  ] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCustomPeriode, setShowCustomPeriode] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataPendaftaran | null>(null);
  const [showSendEmailValidUkt, setShowSendEmailValidUkt] = useState(false);
  const [showConfirmationLoginBack, setShowConfirmationLoginBack] =
    useState(false);
  const [showPindahProdi, setShowPindahProdi] = useState(false);

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: patchDataUnvalidIsiUkt,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataPendaftaran"] });
      setShowConfirmation(false);
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });
  const {
    mutate: mutateSendNotifValidIsiUkt,
    isPending: isPendingSendNotifValidIsiUkt,
  } = useMutation({
    mutationFn: postDataSendNotifEmailValidIsiUkt,
    onSuccess: (data) => {
      setShowSendEmailValidUkt(false);
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const { mutate: mutateLoginBypass, isPending: isPendingLoginBypass } =
    useMutation({
      mutationFn: loginBypassMahasiswa,
      onSuccess: (data) => {
        toast.success(data?.message || "Success.");
        window.location.assign("/");
      },
      onError: (error: AxiosError) => {
        const err = error as AxiosError<{ message?: string }>;
        toast.error(
          err?.response?.data?.message || "An unexpected error occurred.",
        );
      },
    });

  const {
    mutate: mutateUnvalidPendaftaran,
    isPending: isPendingUnvalidPendaftaran,
  } = useMutation({
    mutationFn: patchDataUnvalidStatusPendaftaran,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataPendaftaran"] });
      setShowConfirmationUnvalidPendaftaran(false);
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleUnvalid = () => {
    mutate({
      id_data_pendaftaran: String(selectedRow?.id_data_pendaftaran) || "",
    });
  };

  const handleSendNotifEmailValidIsiUKT = () => {
    mutateSendNotifValidIsiUkt({
      id_data_pendaftaran: String(selectedRow?.id_data_pendaftaran) || "",
    });
  };

  const handleUnvalidPendaftaran = () => {
    mutateUnvalidPendaftaran({
      id_data_pendaftaran: String(selectedRow?.id_data_pendaftaran) || "",
    });
  };

  return (
    <React.Fragment>
      <div className="flex items-center gap-x-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-8 h-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => {
                setShowConfirmationLoginBack(true);
                setSelectedRow(row);
              }}
            >
              Login Sebagai Siswa
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setShowCustomPeriode(true);
                setSelectedRow(row);
              }}
            >
              Set Periode Custom
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setShowPindahProdi(true);
                setSelectedRow(row);
              }}
            >
              Pindah Prodi
            </DropdownMenuItem>
            {row.is_valid_pendaftaran ? (
              <DropdownMenuItem
                onClick={() => {
                  setShowConfirmationUnvalidPendaftaran(true);
                  setSelectedRow(row);
                }}
              >
                Unvalid Pendaftaran
              </DropdownMenuItem>
            ) : (
              ""
            )}
            {row.is_valid_isi_ukt && !row.is_daftar_kembali ? (
              <DropdownMenuItem
                onClick={() => {
                  setSelectedRow(row);
                  setShowSendEmailValidUkt(true);
                }}
              >
                Send Notif Email Sudah Valid UKT
              </DropdownMenuItem>
            ) : (
              ""
            )}
            {row.is_valid_isi_ukt ? (
              <DropdownMenuItem
                onClick={() => {
                  setShowConfirmation(true);
                  setSelectedRow(row);
                }}
              >
                Unvalid Data Penunjang UKT
              </DropdownMenuItem>
            ) : (
              ""
            )}
            <DropdownMenuSeparator />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Modal
        show={showCustomPeriode}
        onClose={() => setShowCustomPeriode(false)}
      >
        <FormPeriodeCustom
          selectedRow={selectedRow}
          closeModal={() => setShowCustomPeriode(false)}
        />
      </Modal>

      <Modal show={showConfirmation} onClose={() => setShowConfirmation(false)}>
        <FormHeader
          title="Konfirmasi Unvalid Status Isi Data Penunjang UKT"
          description={`Aapakah anda yakin akan meng-unvalid Status Isi Data Penunjang UKT peserta ${selectedRow?.data_siswa?.nama_lengkap}?`}
          icon={<></>}
        />
        <div className="mt-5 flex gap-3">
          <Button
            variant={"outline"}
            onClick={() => setShowConfirmation(false)}
            disabled={isPending}
          >
            Batal
          </Button>
          <Button onClick={handleUnvalid} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Ya, Unvalid"
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        show={showSendEmailValidUkt}
        onClose={() => setShowSendEmailValidUkt(false)}
      >
        <FormHeader
          title="Konfirmasi Send Notif Peserta Valid Isi UKT"
          description={`Aapakah anda yakin akan mengirim notifikasi ke peserta valid isi UKT ${selectedRow?.data_siswa?.nama_lengkap}?`}
          icon={<></>}
        />
        <div className="mt-5 flex gap-3">
          <Button
            variant={"outline"}
            onClick={() => setShowSendEmailValidUkt(false)}
            disabled={isPendingSendNotifValidIsiUkt}
          >
            Batal
          </Button>
          <Button
            onClick={handleSendNotifEmailValidIsiUKT}
            disabled={isPendingSendNotifValidIsiUkt}
          >
            {isPendingSendNotifValidIsiUkt ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Ya, Kirim Notif ke Email"
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        show={showConfirmationLoginBack}
        onClose={() => setShowConfirmationLoginBack(false)}
      >
        <FormHeader
          title={`Login sebagai ${selectedRow?.data_siswa?.nama_lengkap}`}
          description={`Apakah anda yakin akan login sebagai ${selectedRow?.data_siswa?.nama_lengkap}?`}
          icon={<IoLogIn />}
        />
        <div className="mt-5 flex gap-3">
          <Button
            variant={"outline"}
            onClick={() => setShowConfirmationLoginBack(false)}
            disabled={isPendingLoginBypass}
          >
            Batal
          </Button>
          <Button
            variant={"default"}
            onClick={() =>
              mutateLoginBypass(selectedRow?.data_siswa?.email || "")
            }
            disabled={isPendingLoginBypass}
          >
            {isPendingLoginBypass ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Ya, Login"
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        show={showConfirmationUnvalidPendaftaran}
        onClose={() => setShowConfirmationUnvalidPendaftaran(false)}
      >
        <FormHeader
          title="Konfirmasi Unvalid Status Pendaftaran"
          description={`Aapakah anda yakin akan meng-unvalid status pendaftaran peserta ${selectedRow?.data_siswa?.nama_lengkap}?`}
          icon={<></>}
        />
        <div className="mt-5 flex gap-3">
          <Button
            variant={"outline"}
            onClick={() => setShowConfirmationUnvalidPendaftaran(false)}
            disabled={isPendingUnvalidPendaftaran}
          >
            Batal
          </Button>
          <Button
            onClick={handleUnvalidPendaftaran}
            disabled={isPendingUnvalidPendaftaran}
          >
            {isPendingUnvalidPendaftaran ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Ya, Unvalid"
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        show={showPindahProdi}
        onClose={() => setShowPindahProdi(false)}
      >
        <FormHeader
          title="Pindah Prodi"
          description={`Pindah program studi untuk peserta ${selectedRow?.data_siswa?.nama_lengkap}`}
          icon={<></>}
        />
        <FormPindahProdi
          selectedRow={selectedRow}
          closeModal={() => setShowPindahProdi(false)}
        />
      </Modal>
    </React.Fragment>
  );
}
